import type { SavedMatchItem, SavedMatchKind } from "../types/api";

// Labels, cell values and the Markdown export of the superadmin Saved matches tab
// (components/SavedMatchesTab.tsx). Pure, so the export can be run outside the browser.

export const KIND_LABELS: Record<SavedMatchKind, string> = {
  manual: "Current period",
  carried: "Prior-period item",
  released: "Un-matched auto-match",
  rejected: "Rejected prior-period clear",
};

export function scopeOf(i: SavedMatchItem): string {
  return i.customer_key ? `${i.recon_book} / ${i.customer_key}` : i.recon_book;
}

// The viewer's local calendar day, "YYYY-MM-DD", so a date filter agrees with the
// local timestamps the table prints.
export function day(t: string): string {
  if (!t) return "";
  const d = new Date(t);
  if (isNaN(d.getTime())) return t.slice(0, 10);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Only the newest run month's outcome is shown, so "not applied" means not applied there.
export const notApplied = (i: SavedMatchItem) => !!i.last_run && !i.last_run.applied;

export function outcomeLabel(i: SavedMatchItem): string {
  const r = i.last_run;
  if (!r) return i.status === "active" && i.kind !== "rejected" ? "No run yet" : "";
  return r.applied ? (r.status === "Matched" ? "Applied" : `Applied · ${r.status}`) : r.status;
}

const KIND_ORDER = Object.keys(KIND_LABELS) as SavedMatchKind[];

// One line per entry, grouped under one heading per insurer / book / kind so nothing is
// repeated per line; problems lead each group. The stored entry stays last as one-line
// JSON, verbatim, so a replay of the file applies exactly the keys prod auto-applies.
export function toMarkdown(items: SavedMatchItem[], total: number, now = new Date()): string {
  const groups = new Map<string, SavedMatchItem[]>();
  const scopes = new Map<string, SavedMatchItem[]>();
  const sorted = [...items].sort((a, b) =>
    a.insurer.localeCompare(b.insurer) || scopeOf(a).localeCompare(scopeOf(b))
    || KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
  for (const i of sorted) {
    const s = `${i.insurer} · ${scopeOf(i)}`;
    const g = `${s} · ${KIND_LABELS[i.kind]}`;
    (groups.get(g) ?? groups.set(g, []).get(g)!).push(i);
    (scopes.get(s) ?? scopes.set(s, []).get(s)!).push(i);
  }

  const out = [
    "# Saved matches export",
    `Exported ${now.toISOString().slice(0, 16).replace("T", " ")} UTC · ${items.length} of ${total} entries`
      + (items.length < total ? " (filtered)" : ""),
    "",
    "| Insurer · book | Newest run | Active | Applied | Not applied | No run yet | Removed |",
    "|---|---|---|---|---|---|---|",
  ];
  for (const [s, list] of scopes) {
    const active = list.filter((i) => i.status === "active");
    const month = list.find((i) => i.last_run)?.last_run?.month ?? "-";
    out.push(`| ${s} | ${month} | ${active.length} | ${active.filter((i) => i.last_run?.applied).length}`
      + ` | ${active.filter(notApplied).length} | ${active.filter((i) => outcomeLabel(i) === "No run yet").length}`
      + ` | ${list.length - active.length} |`);
  }
  out.push("", "Line: saved by and date | outcome in the newest run | stored entry (exact JSON).",
    "Rejected prior-period clears have no run outcome, so they count as Active only.");

  for (const [g, list] of groups) {
    out.push("", `## ${g}`);
    const active = list.filter((i) => i.status === "active");
    const removed = list.filter((i) => i.status !== "active");
    for (const i of [...active.filter(notApplied), ...active.filter((i) => !notApplied(i))]) {
      const r = i.last_run;
      const outcome = !r ? (outcomeLabel(i) || "-")
        : r.applied ? (r.status === "Matched" ? "applied" : `applied (${r.status})`)
        : `NOT APPLIED${r.status === "Not applied" ? "" : ` (${r.status})`}${r.reason ? `: ${r.reason}` : ""}`;
      out.push(`- ${savedBy(i)} | ${outcome} | ${JSON.stringify(i.entry)}`);
    }
    if (removed.length) {
      out.push("### Removed");
      for (const i of removed) {
        out.push(`- ${savedBy(i)} | removed ${i.removed_by || "unknown user"} ${day(i.removed_at)}`
          + ` | ${JSON.stringify(i.entry)}`);
      }
    }
  }
  return out.join("\n") + "\n";
}

function savedBy(i: SavedMatchItem): string {
  return i.saved_by || i.saved_at ? `${i.saved_by || "unknown user"} ${day(i.saved_at)}` : "saved before history";
}
